import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'dark' | 'outline' | 'quiet' | 'danger'
  icon?: ReactNode
}

export function Button({ variant = 'primary', icon, className = '', children, ...props }: ButtonProps) {
  return (
    <button className={`button button--${variant} ${className}`} {...props}>
      <span>{children}</span>
      {icon ? <span className="button__icon" aria-hidden="true">{icon}</span> : null}
    </button>
  )
}

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string
  hint?: string
  error?: string
}

export function Field({ label, hint, error, id, className = '', ...props }: FieldProps) {
  const fieldId = id ?? props.name
  const descriptionId = error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined
  return (
    <div className={`field ${className}`}>
      <label className="field__label" htmlFor={fieldId}>{label}</label>
      <input
        id={fieldId}
        className={`field__control ${error ? 'field__control--error' : ''}`}
        aria-invalid={Boolean(error)}
        aria-describedby={descriptionId}
        {...props}
      />
      {error ? <span id={descriptionId} className="field__error">{error}</span> : null}
      {!error && hint ? <span id={descriptionId} className="field__hint">{hint}</span> : null}
    </div>
  )
}

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string
  hint?: string
}

export function TextArea({ label, hint, id, className = '', ...props }: TextAreaProps) {
  const fieldId = id ?? props.name
  return (
    <div className={`field ${className}`}>
      <label className="field__label" htmlFor={fieldId}>{label}</label>
      <textarea id={fieldId} className="field__control field__control--textarea" {...props} />
      {hint ? <span className="field__hint">{hint}</span> : null}
    </div>
  )
}

export function InlineNotice({ tone = 'info', children }: { tone?: 'info' | 'success' | 'error'; children: ReactNode }) {
  return <div className={`notice notice--${tone}`} role={tone === 'error' ? 'alert' : 'status'}>{children}</div>
}
